import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, X, FileText, Loader2 } from "lucide-react";
import { api, egp, errMsg } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ACard, AH1, ABtn, Status, METHOD, fmtDate } from "./ui";

const ReceiptDialog = ({ p, onClose }) => {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    if (!p) return undefined;
    let u;
    api.get(`/payments/${p.id}/receipt`, { responseType: "blob" }).then((r) => { u = URL.createObjectURL(r.data); setUrl(u); }).catch((e) => toast.error(errMsg(e)));
    return () => { if (u) URL.revokeObjectURL(u); setUrl(null); };
  }, [p]);
  return (
    <Dialog open={!!p} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl bg-[var(--kh-ws-card)] border-white/10 text-slate-100" data-testid="receipt-dialog">
        <DialogHeader><DialogTitle className="text-right">إيصال الدفع</DialogTitle></DialogHeader>
        {!url ? <Loader2 className="animate-spin mx-auto" /> : p?.receipt_type === "application/pdf" ? (
          <iframe src={url} title="receipt" className="w-full h-[70vh] rounded-lg bg-white" />
        ) : <img src={url} alt="إيصال" className="max-h-[70vh] mx-auto rounded-lg" data-testid="receipt-image" />}
      </DialogContent>
    </Dialog>
  );
};

export default function AdminPayments() {
  const [status, setStatus] = useState("pending");
  const [list, setList] = useState([]);
  const [view, setView] = useState(null);
  const load = () => api.get(`/admin/payments?status=${status}`).then((r) => setList(r.data));
  useEffect(() => { load(); }, [status]); // eslint-disable-line react-hooks/exhaustive-deps

  const approve = async (p) => {
    try { await api.post(`/admin/payments/${p.id}/approve`); toast.success("تم القبول وتفعيل الكورس"); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const reject = async (p) => {
    const note = window.prompt("سبب الرفض (يظهر للطالب)", "");
    if (note === null) return;
    try { await api.post(`/admin/payments/${p.id}/reject`, { note }); toast.success("تم رفض الطلب"); load(); } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div data-testid="admin-payments">
      <AH1>طلبات الدفع</AH1>
      <div className="flex flex-wrap gap-2 mb-6">
        {[["pending", "قيد المراجعة"], ["approved", "مقبولة"], ["rejected", "مرفوضة"], ["all", "الكل"]].map(([v, l]) => (
          <button key={v} onClick={() => setStatus(v)} data-testid={`payments-filter-${v}`}
            className={`px-4 py-2 rounded-full text-sm ${status === v ? "bg-[var(--kh-violet)] text-white" : "bg-white/5 text-slate-400 hover:text-white"}`}>{l}</button>
        ))}
      </div>
      <div className="space-y-3">
        {list.length === 0 && <p className="text-slate-500" data-testid="payments-empty">لا توجد طلبات.</p>}
        {list.map((p) => (
          <ACard key={p.id} className="grid md:grid-cols-12 gap-4 items-center" data-testid={`admin-payment-${p.id}`}>
            <div className="md:col-span-4 min-w-0">
              <p className="font-semibold truncate">{p.user?.name}</p>
              <p className="text-xs text-slate-400 truncate" dir="ltr">{p.user?.email}</p>
            </div>
            <div className="md:col-span-4 text-sm">
              <p className="truncate">{p.course_title}</p>
              <p className="text-xs text-slate-400">{METHOD[p.method]} • {egp(p.amount)} • من <span dir="ltr">{p.sender_phone}</span>{p.reference && ` • مرجع: ${p.reference}`}</p>
              <p className="text-[11px] text-slate-500">{fmtDate(p.created_at)}</p>
            </div>
            <div className="md:col-span-4 flex flex-wrap items-center gap-2 md:justify-end">
              <Status s={p.status} testid={`payment-status-${p.id}`} />
              <ABtn variant="ghost" onClick={() => setView(p)} data-testid={`view-receipt-${p.id}`}><FileText size={15} />الإيصال</ABtn>
              {p.status === "pending" && <ABtn variant="success" onClick={() => approve(p)} data-testid={`approve-payment-${p.id}`}><Check size={15} />قبول</ABtn>}
              {p.status === "pending" && <ABtn variant="danger" onClick={() => reject(p)} data-testid={`reject-payment-${p.id}`}><X size={15} />رفض</ABtn>}
            </div>
            {p.admin_note && <p className="md:col-span-12 text-xs text-red-300">ملاحظة: {p.admin_note}</p>}
          </ACard>
        ))}
      </div>
      <ReceiptDialog p={view} onClose={() => setView(null)} />
    </div>
  );
}
